import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useTranslation } from "next-i18next";

import { Modal } from "common/ui/Modal";
import { Stack } from "common/ui/Stack";
import { Paragraph } from "common/ui/Paragraph";
import { BrokerFormInputs } from "common/features/account/components/BrokerFormInputs";

import { brokerProfileValidation } from "common/features/account/validations/brokerProfileValidation";
import type { BrokerProfileSchema } from "common/features/account/validations/brokerProfileValidation";
import { editBrokerProfile } from "common/features/account/api/editBrokerProfile";
import { AddressInput } from "common/middleware/api.services";

interface Props {
  close: () => void,
  onSuccess?: () => void,
}

export const BrokerFeatureRequestModal = ({
  close,
  onSuccess,
}: Props) => {
  const { t } = useTranslation(["common", "validation"]);
  const methods = useForm<BrokerProfileSchema>({
    mode: "onBlur",
    resolver: yupResolver(brokerProfileValidation),
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [err, setErr] = useState("");

  const saveBrokerProfile = async (values: BrokerProfileSchema) => {
    try {
      await editBrokerProfile({
        licenseNumber: values.brokerLicenseNum,
        licenseJurisdiction: values.brokerLicenseJurisdiction,
        licenseExpiryDate: new Date(values.brokerLicenseExpiryDate),
        brokerageName: values.brokerageName,
        brokerageTaxNumber: values.brokerageTaxNum,
        brokeragePhoneNumber: values.brokeragePhone,
        brokerageAddress: new AddressInput(values.brokerageAddress),
      });

      if (onSuccess) {
        onSuccess();
      }

      close();
    } catch (err) {
      setErr(t("somethingWentWrong", { ns: "validation" }));
    }
  };

  return (
    <Modal
      title={t("brokerFeatureRequestModal.unlock")}
      close={close}
      error={err}
      primaryAction={{
        text: t("common.submit"),
        onClick: handleSubmit(saveBrokerProfile),
      }}
      secondaryAction={{
        text: t("common.cancel"),
        onClick: close,
      }}
      isProcessing={isSubmitting}
    >
      <Stack direction="column" gap={16}>
        <Paragraph variant="p1">
          {t("brokerFeatureRequestModal.fillOut")}
        </Paragraph>
        <FormProvider {...methods}>
          <BrokerFormInputs />
        </FormProvider>
      </Stack>
    </Modal>
  )
}
